import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type {
  SharedHistoryMarker,
  SharedHistoryMarkerPosition,
} from "@/features/chat/utils/shared-history"

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

type SharedHistoryMarkerProps = {
  markers: SharedHistoryMarker[]
  onManageSharing?: (messageId?: string) => void
}

const formatSharedDate = (value?: string) => {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toLocaleString()
}

const accessModeLabels = (
  t: ReturnType<typeof useTranslation>["t"]
): Record<SharedHistoryMarker["accessMode"], string> => ({
  full_history: t("sharing.access_full_history_badge", { defaultValue: "Full history" }),
  partial_history: t("sharing.access_partial_history_badge", { defaultValue: "New messages" }),
  specific_message: t("sharing.access_specific_message_badge", { defaultValue: "Single message" }),
})

export function SharedHistoryMarkerLine({ markers, onManageSharing }: SharedHistoryMarkerProps) {
  const { t } = useTranslation()

  const labels = useMemo(() => accessModeLabels(t), [t])

  const getMarkerLabel = (position: SharedHistoryMarkerPosition, displayName: string) => {
    const labelsByPosition: Record<SharedHistoryMarkerPosition, string> = {
      start: t("sharing.shared_from_here", {
        defaultValue: "Shared from here for {{name}}",
        name: displayName,
      }),
      end: t("sharing.shared_until_here", {
        defaultValue: "Shared until here for {{name}}",
        name: displayName,
      }),
      above: t("sharing.shared_above", {
        defaultValue: "Shared above for {{name}}",
        name: displayName,
      }),
      below: t("sharing.shared_below", {
        defaultValue: "Shared below for {{name}}",
        name: displayName,
      }),
    }
    return labelsByPosition[position]
  }

  const groupedMarkers = useMemo(() => {
    const groups = new Map<
      string,
      { position: SharedHistoryMarkerPosition; messageId?: string; markers: SharedHistoryMarker[] }
    >()
    markers.forEach((marker) => {
      const groupKey = `${marker.position}:${marker.messageId ?? "none"}`
      const existing = groups.get(groupKey)
      if (existing) {
        existing.markers.push(marker)
      } else {
        groups.set(groupKey, {
          position: marker.position,
          messageId: marker.messageId,
          markers: [marker],
        })
      }
    })
    return Array.from(groups.values())
  }, [markers])

  const getGroupDisplayName = (groupMarkers: SharedHistoryMarker[]) => {
    if (groupMarkers.length === 0) {
      return t("sharing.user", { defaultValue: "User" })
    }
    const names = groupMarkers.map(
      (marker) => marker.displayName || t("sharing.user", { defaultValue: "User" })
    )
    if (names.length === 1) return names[0]
    return `${names[0]} +${names.length - 1}`
  }

  if (!markers.length) return null

  return (
    <div className="flex flex-col gap-2">
      {groupedMarkers.map((group) => {
        const displayName = getGroupDisplayName(group.markers)
        const lineLabel = getMarkerLabel(group.position, displayName)

        return (
          <Tooltip key={`${group.position}-${group.messageId ?? "none"}`}>
            <TooltipTrigger asChild>
              <button
                className="group text-muted-foreground hover:text-foreground flex w-full items-center gap-3 text-xs transition"
                type="button"
                onClick={() => onManageSharing?.(group.messageId)}
              >
                <span className="bg-border/60 group-hover:bg-border h-px flex-1 transition" />
                <span className="border-border/60 bg-background rounded-md border px-2 py-0.5 text-[11px] font-semibold">
                  {lineLabel}
                </span>
                <span className="bg-border/60 group-hover:bg-border h-px flex-1 transition" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px] space-y-2 text-xs">
              <ul className="space-y-2">
                {group.markers.map((marker, index) => {
                  const markerName =
                    marker.displayName || t("sharing.user", { defaultValue: "User" })
                  const sharedAt = formatSharedDate(marker.sharedAt)
                  return (
                    <li
                      key={`${marker.id}-${marker.position}`}
                      className={index > 0 ? "border-border/60 border-t pt-2" : undefined}
                    >
                      <p className="font-semibold">{markerName}</p>
                      <p>
                        {t("sharing.shared_access_mode", {
                          defaultValue: "Access: {{mode}}",
                          mode: labels[marker.accessMode],
                        })}
                      </p>
                      {sharedAt && (
                        <p>
                          {t("sharing.shared_at", {
                            defaultValue: "Shared at {{date}}",
                            date: sharedAt,
                          })}
                        </p>
                      )}
                    </li>
                  )
                })}
              </ul>
              {onManageSharing && (
                <p className="text-muted-foreground">
                  {t("sharing.shared_manage_hint", {
                    defaultValue: "Click to manage sharing",
                  })}
                </p>
              )}
            </TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}
